// Package imports:
import React, { useState, useMemo } from 'react';
import cx from 'classnames';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Link from '../../ui-elements/Link/Link';
import ColoredNumber from '../../ui-elements/ColoredNumber/ColoredNumber';
import DelayOrClosedLabel from '../DelayOrClosedLabel/DelayOrClosedLabel';
import DatePicker from '../DatePicker/DatePicker';
import PriceRange from '../../ui-elements/PriceRange/PriceRange';
import LmdTable from '../../ui-elements/Table/LmdTable';
import LmdSingleLineTable from '../../ui-elements/Table/LmdSingleLineTable';
// Service imports:
import { convertToPercentage, formatNumber, displayQuantityShorthand, getAPItoday } from '../../services/utils';
import { useApiLmdData, useApiLmdDataMappedByString } from '../../services/apiHooks';
// Type imports:
import { IApiLmdStatistics } from '../../types/HlutabrefTypes';
import { IApiLmdSnapshot, IApiLmdHistory } from '../../types/SkuldabrefTypes';
import { ApiLmdData, IDefaultProps } from '../../types/Types';

interface IOwnProps {
    symbol?: string,
    exchange?: string,
    market?: string,
    indexed?: boolean,
    showDatepicker?: boolean
}

interface ICombinedData {
    symbol: string | null,
    last_yield: number | null,
    last_yield_diff: number | null,
    bid_yield: number | null,
    ask_yield: number | null,
    last_price: number | null,
    last_price_diff: number | null,
    bid: number | null,
    ask: number | null,
    last_duration: number | null,
    intraday_accumulated_turnover: number | null,
    intraday_no_of_trades: number | null,
    weeks4_price_change_per?: number | null,
    year_price_change_per?: number | null,
    months12_price_change_per?: number | null,
    last_trade_dateutc?: string | null,
    months12_high_price?: number | null,
    months12_low_price?: number | null
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

/**
 * Renders a table component for bonds, given the parameters
 * @param symbol If present shows only this symbol. If undefined, show all symbols.
 * @param exchange Specifies the exchange to use.
 * @param market Specifies the market to use.
 * @param indexed is the bond indexed or not (ís. verðtryggt)
 * @param showDatepicker Allow user to choose date of data.
 * @param accessToken Default Props
 * @param refreshRateMs Default Props
 * @returns A JSX Table component
 */
const BondsTable: React.FC<Props> = ({
    symbol,
    exchange = 'XICE',
    market = 'ICECB',
    indexed,
    showDatepicker,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [data] = useApiLmdData<IApiLmdSnapshot[]>(
        (symbol
            ? `/v1/market_data/v1/exchanges/*/markets/*/bonds/${symbol}/delayed_snapshot/`
            : `/v1/market_data/v1/exchanges/${exchange}/active/bonds/delayed_snapshot`
        ),
        accessToken,
        refreshRateMs
    );
    const [currentDate, setCurrentDate, statistics] = useApiLmdDataMappedByString<IApiLmdStatistics[]>(
        getAPItoday(),
        (date: string) => `/v1/market_data/v1/exchanges/${exchange}/markets/${market}/tradables/${symbol ?? '*'}/statistics_delayed?trading_date=${date}`,
        accessToken,
        refreshRateMs
    );
    const [, setHistoryDate, history] = useApiLmdDataMappedByString<IApiLmdHistory[]>(
        getAPItoday(),
        (date: string) => `/v1/market_data/v1/exchanges/${exchange}/markets/${market}/tradables/${symbol ?? '*'}/history?from_date=${date}&to_date=${date}`,
        accessToken
    );
    const [bondsToUse, setBondsToUse] = useState<(string | null)[]>([]);

    const isToday = useMemo(() => (currentDate === getAPItoday()), [ currentDate ])

    const calculatePriceRangeRatio = (low: number | null, value: number | null, high: number | null) => {
        // Null check.
        if (low === null || value === null || high === null) return null;
        const formattedLowNumber = formatNumber(low, '-', 2);
        const formattedHighNumber = formatNumber(high, null, 2);
        // If equal, value is in middle
        if (formattedLowNumber === formattedHighNumber) return 0.5;
        return (value - low) / (high - low);
    }

    const displayData = useMemo(() => {
        const newData: ICombinedData[] = [];

        if (isToday) {
            if (data === null || data.data === null || data.error instanceof Error || statistics?.data === undefined || statistics.error instanceof Error) {
                return newData;
            }
            for (let entry of data.data) {
                if (symbol || (entry.index_name !== null) === indexed) {
                    const statsEntry = statistics.data?.find(e => e.symbol === entry.symbol);
                    newData.push({
                        symbol: entry.symbol,
                        last_yield: entry.last_yield ?? 0,
                        last_yield_diff: entry.last_yield_diff ?? 0,
                        bid_yield: entry.bid_yield ?? 0,
                        ask_yield: entry.ask_yield ?? 0,
                        last_price: entry.last_price ?? 0,
                        last_price_diff: entry.last_price_diff ?? 0,
                        bid: entry.bid ?? 0,
                        ask: entry.ask ?? 0,
                        last_duration: entry.last_duration ?? 0,
                        intraday_accumulated_turnover: entry.intraday_accumulated_turnover ?? 0,
                        intraday_no_of_trades: entry.intraday_no_of_trades ?? 0,
                        months12_high_price: statsEntry?.months12_high_price ?? 0,
                        months12_low_price: statsEntry?.months12_low_price ?? 0
                    });
                }
            }
            if (bondsToUse.length === 0) {
                const btu = newData.map(entry => entry.symbol);
                setBondsToUse(btu);
            }
        } else {
            if (history === undefined || history.data === null || history.error instanceof Error || statistics?.data === undefined || statistics.error instanceof Error) {
                return newData;
            }
            for (let entry of history.data) {
                const i = bondsToUse.findIndex(e => e === entry.symbol);
                if (i !== -1) {
                    const statsEntry = statistics.data?.find(e => e.symbol === entry.symbol);
                    const dataEntry = data !== null && !(data.error instanceof Error) ? data.data?.find(e => e.symbol === entry.symbol) : null;
                    newData.push({
                        symbol: entry.symbol,
                        last_yield: entry.close_yield,
                        last_yield_diff: statsEntry?.yesterdays_yield_change_nom ?? 0,
                        bid_yield: null,
                        ask_yield: null,
                        last_price: entry.close,
                        last_price_diff: statsEntry?.yesterdays_price_change_per ?? 0,
                        bid: null,
                        ask: null,
                        last_duration: dataEntry?.last_duration ?? 0,
                        intraday_accumulated_turnover: statsEntry?.yesterdays_value_traded ?? 0,
                        intraday_no_of_trades: entry.number_of_trades,
                        months12_high_price: statsEntry?.months12_high_price,
                        months12_low_price: statsEntry?.months12_low_price
                    });
                }
            }
        }
        return newData.sort((a, b) => {
            return a.symbol === null && b.symbol === null
                ? 0
                : a.symbol === null
                ? -1
                : b.symbol === null
                ? 1
                : a.symbol > b.symbol
                ? 1
                : -1;
        });
    }, [data, statistics, history]);

    const apiData: ApiLmdData<ICombinedData[]> = {
        data: displayData,
        error: data.error ?? statistics.error ?? history.error ?? null
    }

    return (
        <DisplayBox
            className={cx('KCL_bonds-table', {'hide-symbol': symbol})}
            title={symbol ?? `${indexed ? 'Verðtryggð' : 'Óverðtryggð'} skuldabréf`}
            statusComponent={<DelayOrClosedLabel closeType='bonds' />}
            asideComponent={showDatepicker
                ? <DatePicker
                    maxDate={new Date()}
                    size="sm"
                    selectedDate={new Date(currentDate)}
                    setSelectedDate={(newDate) => { setCurrentDate(getAPItoday(newDate)); setHistoryDate(getAPItoday(newDate)); }}
                    lang="is"
                    id={`date-picker-${indexed ? 'indexed' : 'nonindexed'}`}
                />
                : undefined
            }
            {...props}
        >
            {symbol
                ? <LmdSingleLineTable
                    apiData={apiData}
                    columns={[{
                        title: 'Auðkenni',
                        dataAccessor: ({symbol}) => <Link url={`/Markadir/Skuldabref/${symbol}`} linkSize='15'>{symbol}</Link>,
                        textAlign: 'left',
                    }, {
                        title: 'Áv. krafa',
                        dataAccessor: ({last_yield}) => convertToPercentage(last_yield, true, 2),
                        textAlign : 'left',
                    }, {
                        title: 'Kröfubr.',
                        dataAccessor: ({last_yield_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_yield_diff !== null}
                                    sign={!last_yield_diff ? 'neutral' : last_yield_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {formatNumber(last_yield_diff ? last_yield_diff*10000 : 0, null, 0)} pkt.
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'left',
                    }, {
                        title: 'Kaupkrafa',
                        dataAccessor: ({bid_yield}) => convertToPercentage(bid_yield, true, 2),
                        textAlign: 'left',
                    }, {
                        title: 'Sölukrafa',
                        dataAccessor: ({ask_yield}) => convertToPercentage(ask_yield, true, 2),
                        textAlign: 'left',
                    }, {
                        title: 'Verð',
                        dataAccessor: ({last_price}) => formatNumber(last_price),
                        textAlign: 'left',
                    }, {
                        title: 'Verðbr.',
                        dataAccessor: ({last_price_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_price_diff !== null}
                                    sign={!last_price_diff ? 'neutral' : last_price_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {formatNumber(last_price_diff, null, 0)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'left',
                    }, {
                        title: 'Kaupverð',
                        dataAccessor: ({bid}) => formatNumber(bid),
                        textAlign: 'left',
                    }, {
                        title: 'Söluverð',
                        dataAccessor: ({ask}) => formatNumber(ask),
                        textAlign: 'left',
                    }, {
                        title: 'Líftími',
                        dataAccessor: ({last_duration}) => formatNumber(last_duration),
                        textAlign: 'left',
                    }, {
                        title: 'Fj. viðskipta',
                        dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades,
                        textAlign: 'left',
                    }, {
                        title: 'Velta',
                        dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                        textAlign: 'left',
                    }, {
                        title: 'Árs bil',
                        dataAccessor: ({months12_low_price, months12_high_price, last_price, symbol}) => <PriceRange low={months12_low_price ?? null} high={months12_high_price ?? null} value={last_price} />,
                    }]}
                    tableSize="lg"
                />
                : <LmdTable
                    apiData={apiData}
                    columns={[{
                        title: 'Auðkenni',
                        dataAccessor: ({symbol}) => <Link url={`/Markadir/Skuldabref/${symbol}`} linkSize='15'>{symbol}</Link>,
                        textAlign: 'left',
                        simpleSortable: ({symbol}) => symbol
                    }, {
                        title: 'Áv. krafa',
                        dataAccessor: ({last_yield}) => convertToPercentage(last_yield, true, 2),
                        textAlign: 'right',
                        simpleSortable: ({last_yield}) => last_yield
                    }, {
                        title: 'Kröfubr.',
                        dataAccessor: ({last_yield_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_yield_diff !== null}
                                    sign={!last_yield_diff ? 'neutral' : last_yield_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {formatNumber(last_yield_diff ? last_yield_diff*10000 : 0, null, 0)} pkt.
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({last_yield_diff}) => last_yield_diff
                    }, {
                        title: 'Kaupkrafa',
                        dataAccessor: ({bid_yield}) => convertToPercentage(bid_yield, true, 2),
                        textAlign: 'right',
                        simpleSortable: ({bid_yield}) => bid_yield
                    }, {
                        title: 'Sölukrafa',
                        dataAccessor: ({ask_yield}) => convertToPercentage(ask_yield, true, 2),
                        textAlign: 'right',
                        simpleSortable: ({ask_yield}) => ask_yield
                    }, {
                        title: 'Verð',
                        dataAccessor: ({last_price}) => formatNumber(last_price),
                        textAlign: 'right',
                        simpleSortable: ({last_price}) => last_price
                    }, {
                        title: 'Verðbr.',
                        dataAccessor: ({last_price_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_price_diff !== null}
                                    sign={!last_price_diff ? 'neutral' : last_price_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {formatNumber(last_price_diff, null, 0)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({last_price_diff}) => last_price_diff
                    }, {
                        title: 'Kaupverð',
                        dataAccessor: ({bid}) => formatNumber(bid),
                        textAlign: 'right',
                        simpleSortable: ({bid}) => bid
                    }, {
                        title: 'Söluverð',
                        dataAccessor: ({ask}) => formatNumber(ask),
                        textAlign: 'right',
                        simpleSortable: ({ask}) => ask
                    }, {
                        title: 'Líftími',
                        dataAccessor: ({last_duration}) => formatNumber(last_duration),
                        textAlign: 'right',
                        simpleSortable: ({last_duration}) => last_duration
                    }, {
                        title: 'Fj. viðskipta',
                        dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades,
                        textAlign: 'right',
                        simpleSortable: ({intraday_no_of_trades}) => intraday_no_of_trades
                    }, {
                        title: 'Velta',
                        dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                        textAlign: 'right',
                        simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
                    }, {
                        title: 'Árs bil',
                        dataAccessor: ({months12_low_price, months12_high_price, last_price}) => <PriceRange low={months12_low_price ?? null} high={months12_high_price ?? null} value={last_price} />,
                        simpleSortable: ({months12_low_price, last_price, months12_high_price}) => calculatePriceRangeRatio(months12_low_price ?? null, last_price, months12_high_price ?? null)
                    }]}
                    tableSize="lg"
                />
            }
        </DisplayBox> 
    );
}

export default BondsTable;